import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { 
  Award, 
  Plus, 
  Edit, 
  Trash2, 
  Calendar,
  ExternalLink,
  Download,
  X
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const CertificatesManagement = () => {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm();

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await api.get('/api/profile/certificates/');
      console.log('Certificates response:', response.data);
      
      // Handle different response structures
      const certificatesData = Array.isArray(response.data) ? response.data : response.data?.results || [];
      setCertificates(certificatesData);
    } catch (error) {
      toast.error('Failed to fetch certificates');
      console.error('Error fetching certificates:', error);
      // Set empty array as fallback
      setCertificates([]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (editingId) {
        await api.patch(`/api/profile/certificates/${editingId}/`, data);
        toast.success('Certificate updated successfully');
      } else {
        await api.post('/api/profile/certificates/', data);
        toast.success('Certificate added successfully');
      }
      
      reset();
      setShowForm(false);
      setEditingId(null);
      fetchCertificates();
    } catch (error) {
      toast.error(editingId ? 'Failed to update certificate' : 'Failed to add certificate');
      console.error('Error saving certificate:', error);
    }
  };

  const handleEdit = (certificate) => {
    setEditingId(certificate.id);
    setValue('name', certificate.name);
    setValue('issuing_organization', certificate.issuing_organization);
    setValue('issue_date', certificate.issue_date);
    setValue('expiry_date', certificate.expiry_date);
    setValue('credential_id', certificate.credential_id);
    setValue('credential_url', certificate.credential_url);
    setValue('description', certificate.description);
    setValue('is_permanent', certificate.is_permanent);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) {
      return;
    }

    try {
      await api.delete(`/api/profile/certificates/${id}/`);
      toast.success('Certificate deleted successfully');
      fetchCertificates();
    } catch (error) {
      toast.error('Failed to delete certificate');
      console.error('Error deleting certificate:', error);
    }
  };

  const handleCancel = () => {
    reset();
    setShowForm(false);
    setEditingId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">Certificates & Certifications</h1>
              <button
                onClick={() => setShowForm(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Add Certificate</span>
              </button>
            </div>
          </div>

          <div className="p-6">
            {certificates.length === 0 ? (
              <div className="text-center py-12">
                <Award className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No certificates added</h3>
                <p className="mt-1 text-sm text-gray-500">Add your certificates and certifications to showcase your skills.</p>
                <div className="mt-6">
                  <button
                    onClick={() => setShowForm(true)}
                    className="btn-primary"
                  >
                    Add Certificate
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {Array.isArray(certificates) && certificates.map((certificate) => (
                  <div key={certificate.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <Award className="w-6 h-6 text-purple-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">
                              {certificate.name}
                            </h3>
                            <p className="text-sm text-gray-600">{certificate.issuing_organization}</p>
                          </div>
                        </div>
                        
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-2" />
                            {certificate.issue_date} - {certificate.is_permanent ? 'No Expiry' : certificate.expiry_date}
                          </div>
                          {certificate.credential_id && (
                            <div className="flex items-center text-sm text-gray-500">
                              <span className="font-medium">ID:</span>
                              <span className="ml-1">{certificate.credential_id}</span>
                            </div>
                          )}
                        </div>
                        
                        {certificate.description && (
                          <p className="mt-3 text-sm text-gray-700">{certificate.description}</p>
                        )}

                        {certificate.credential_url && (
                          <div className="mt-3">
                            <a
                              href={certificate.credential_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-sm text-primary-600 hover:text-primary-500"
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              View Certificate
                            </a>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(certificate)}
                          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Edit certificate"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(certificate.id)}
                          className="p-2 text-red-400 hover:text-red-600 transition-colors"
                          title="Delete certificate"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Certificate Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingId ? 'Edit Certificate' : 'Add Certificate'}
                </h3>
                <button
                  onClick={handleCancel}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Certificate Name *
                    </label>
                    <input
                      {...register('name', { required: 'Certificate name is required' })}
                      type="text"
                      className={`input-field ${errors.name ? 'border-red-300 focus:ring-red-500' : ''}`}
                      placeholder="AWS Certified Solutions Architect"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Issuing Organization *
                    </label>
                    <input
                      {...register('issuing_organization', { required: 'Issuing organization is required' })}
                      type="text"
                      className={`input-field ${errors.issuing_organization ? 'border-red-300 focus:ring-red-500' : ''}`}
                      placeholder="Amazon Web Services"
                    />
                    {errors.issuing_organization && (
                      <p className="mt-1 text-sm text-red-600">{errors.issuing_organization.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Issue Date *
                    </label>
                    <input
                      {...register('issue_date', { required: 'Issue date is required' })}
                      type="date"
                      className={`input-field ${errors.issue_date ? 'border-red-300 focus:ring-red-500' : ''}`}
                    />
                    {errors.issue_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.issue_date.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expiry Date
                    </label>
                    <input
                      {...register('expiry_date')}
                      type="date"
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Credential ID
                    </label>
                    <input
                      {...register('credential_id')}
                      type="text"
                      className="input-field"
                      placeholder="AWS-123456789"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Credential URL
                    </label>
                    <input
                      {...register('credential_url', {
                        pattern: {
                          value: /^https?:\/\/.+/,
                          message: 'Please enter a valid URL'
                        }
                      })}
                      type="url"
                      className={`input-field ${errors.credential_url ? 'border-red-300 focus:ring-red-500' : ''}`}
                      placeholder="https://aws.amazon.com/verification"
                    />
                    {errors.credential_url && (
                      <p className="mt-1 text-sm text-red-600">{errors.credential_url.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="input-field"
                    placeholder="Describe the certificate, skills gained, or relevance to your career..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    {...register('is_permanent')}
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    This certificate does not expire
                  </label>
                </div>

                <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    {editingId ? 'Update Certificate' : 'Add Certificate'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CertificatesManagement;
